import { useContext, useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

export const O365Entra = () => {
  const [state, setState] = useState({ data: null, isLoaded: false, error: null });

  const { authState } = useOktaAuth();
  const { userModule } = useContext(UserModuleContext);

  useEffect(() => {
    const fetchO365Info = async () => {
      try {
        const response = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/o365/info`,
            {
              method: "POST",
              headers: {
                Authorization: authState.accessToken.accessToken,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                networkId: userModule.userData["Network ID"],
              }),
            }
        );

        if (!response.ok) {
          let message = `Request failed with status ${response.status}`;
          try {
            const errorData = await response.json();
            message = errorData?.error?.message || errorData?.message || message;
          } catch {
            // response body wasn't JSON — use the status-based message
          }
          throw new Error(message);
        }

        const result = await response.json();
        setState({ data: result, isLoaded: true, error: null });
      } catch (error) {
        setState({ data: null, isLoaded: true, error });
      }
    };

    fetchO365Info();
  }, [authState.accessToken.accessToken, userModule.userData]);

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Failed to load Office 365 info
            </Typography>
            <Typography variant="body2">
              {state.error.message || "Unknown error occurred"}
            </Typography>
          </Box>
        </TableContainer>
    );
  }

  const renderCell = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {value.map((v) => (
                <Chip
                    key={v}
                    label={v}
                    size="small"
                    sx={{
                      backgroundColor: "#fdf0f2",
                      border: `1px solid ${WSU_RED}`,
                      color: WSU_RED,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                />
            ))}
          </Box>
      ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
      );
    }

    const display = typeof value === "boolean" ? value.toString() : value;

    return display !== null && display !== undefined && display !== "" ? (
        <Typography variant="body2">{display}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  const entries = Object.entries(state.data || {}).filter(
      ([key]) => key !== "statusCode"
  );

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Office 365 / Entra
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            {userModule.userData["Network ID"]}
          </Typography>
        </Box>

        <Table>
          <TableBody>
            {entries.map(([key, value]) => (
                <TableRow key={`${key}-row`}>
                  <TableCell sx={labelCellSx}>{key}</TableCell>
                  <TableCell>{renderCell(value)}</TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  );
};